import { useEffect, useMemo, useState } from 'react'
import type {
  AccessHrRoleBody, AccessHrRoleEffect, AccessHrRolePage, AccessHrRoleRow, AccessHrRoleSummary, AccessPeople, ApiFailure,
} from '../api/types'
import type { Api } from '../api'
import { Badge, Button, Card, EmptyState, Loading, Notice, PageTabs, PersonCell, SearchInput, SearchSelect, Segmented } from '../components/ui'
import { DataGrid, type Column } from '../components/DataGrid'
import { GridChromeSlot } from '../components/gridLayout'
import { FailureState } from '../components/FailureState'
import { HistoryTab } from './AccessPersonTabs'
import { HierarchyPreview } from './HierarchyPreview'
import {
  HIERARCHY, ChangedMark, FilterSlot, Meta, Panel, ReportGroups, StatusBadge, Switch, ToggleChip, day, failureOf, messageOf, roleLabel,
} from './accessWords'

/**
 * The Function filter's choice for roles Mindzer sends with no function. It starts with a
 * space because the loader trims every function (050), so no real one can.
 */
const NO_FUNCTION = ' none'

/**
 * ACCESS › HR ROLES, under the new permission model (05 section 7.6).
 *
 * Every Mindzer role, and per role what its holders see: reports, the column
 * groups of those reports (D53), hierarchy and phone. Nothing is set up at the switch hour (D3, D36): a role's
 * holders open nothing until an admin sets it up here.
 *
 * EFFECT BEFORE THE PRESS. "Review changes" asks the same procedure that saves for
 * what the save would do (@DryRun), never on every click: each preview runs the
 * access builder. Save is offered only for the draft that was reviewed, with a
 * reason, and quotes the version the editor read (a change since is refused, 409).
 *
 * Laid out again 27 Oct 2026 with People and Approvals: the filters named above
 * their controls, how far setting up has come in one line, and the role's page as
 * cards: reports and column groups as tiles, hierarchy as three cards to pick from, phone
 * as a switch, and what saving does held on the right.
 */
export function AccessHrRolesTab({ api }: { api: Api }) {
  const [data, setData] = useState<AccessHrRoleRow[] | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [search, setSearch] = useState('')
  const [show, setShow] = useState<'held' | 'all' | 'nobody'>('held')
  const [onlyNotSetUp, setOnlyNotSetUp] = useState(false)
  const [fn, setFn] = useState('')
  const [open, setOpen] = useState<number | null>(null)
  const [chromeSlot, setChromeSlot] = useState<HTMLElement | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api
      .accessHrRoles()
      .then((d) => !cancelled && setData(d.roles))
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, reload])

  // The Function filter: every function Mindzer sends, each with how many roles the Show
  // and "Not configured yet" choices leave it, so a function they empty says 0 before it
  // is picked. A role with no function is a choice of its own.
  const functionOptions = useMemo(() => {
    const rows = data ?? []
    const counted = rows.filter((r) => (show === 'all' || (show === 'held' ? r.people > 0 : r.people === 0)) && (!onlyNotSetUp || !r.setUp))
    const count = (f: string) => String(counted.filter((r) => (f === NO_FUNCTION ? !r.roleFunction : r.roleFunction === f)).length)
    const names = [...new Set(rows.map((r) => r.roleFunction).filter((f): f is string => !!f))].sort((a, b) => a.localeCompare(b))
    // A choice the latest list no longer has stays on offer, so the filter in force is never hidden.
    if (fn && fn !== NO_FUNCTION && !names.includes(fn)) names.push(fn)
    return [
      { value: '', label: 'All functions', trailing: String(counted.length) },
      ...names.map((f) => ({ value: f, label: f, trailing: count(f) })),
      ...(fn === NO_FUNCTION || rows.some((r) => !r.roleFunction)
        ? [{ value: NO_FUNCTION, label: 'No function in Mindzer', trailing: count(NO_FUNCTION) }]
        : []),
    ]
  }, [data, show, onlyNotSetUp, fn])

  const shown = useMemo(() => {
    const s = search.trim().toLowerCase()
    return (data ?? []).filter((r) =>
      (show === 'all' || (show === 'held' ? r.people > 0 : r.people === 0))
      && (!onlyNotSetUp || !r.setUp)
      && (!fn || (fn === NO_FUNCTION ? !r.roleFunction : r.roleFunction === fn))
      && (!s || roleLabel(r).toLowerCase().includes(s) || String(r.roleId).includes(s)))
  }, [data, search, show, onlyNotSetUp, fn])

  const columns = useMemo<Column<AccessHrRoleRow>[]>(
    () => [
      // The role and its function in columns of their own (owner, 5 Nov 2026): the pinned
      // role column's edge is the line between them.
      {
        key: 'role',
        header: 'HR Role',
        align: 'left',
        stickyLeft: true,
        // Null, not '', so a role with no name sorts last both ways (gridSort); the
        // function breaks ties between roles of one name.
        sortValue: (r) => (r.roleName ? roleLabel(r) : null),
        render: (r) => (
          <span className="flex flex-col items-start text-left">
            <span className="font-medium text-(--color-ink)">{r.roleName ?? '(no name)'}</span>
            <span className="text-(length:--text-small) text-(--color-ink-4)">role {r.roleId}{r.roleName ? '' : ' · no name in Mindzer'}</span>
          </span>
        ),
      },
      {
        key: 'function',
        header: 'Function',
        align: 'left',
        sortValue: (r) => r.roleFunction ?? null,
        render: (r) => (r.roleFunction ? <span className="text-(--color-ink)">{r.roleFunction}</span> : <span className="text-(--color-ink-4)">—</span>),
      },
      {
        key: 'setUp',
        header: 'Configured in MBI',
        align: 'left',
        sortValue: (r) => (r.setUp ? 1 : 0),
        render: (r) => (r.setUp ? <Badge tone="good">Configured</Badge> : <Badge tone="warn">Not configured</Badge>),
      },
      // Headers in the owner's words, 30 Oct 2026.
      { key: 'people', header: 'HR Role Users', align: 'right', value: (r) => r.people, render: (r) => String(r.people) },
      { key: 'approved', header: 'Access Approved', align: 'right', value: (r) => r.approved, render: (r) => String(r.approved) },
      { key: 'canEnter', header: 'Can access MBI', align: 'right', value: (r) => r.canEnter, render: (r) => String(r.canEnter) },
      { key: 'reports', header: 'Report Access', align: 'right', value: (r) => r.reports, render: (r) => (r.setUp ? String(r.reports) : <span className="text-(--color-ink-4)">—</span>) },
      { key: 'columnGroups', header: 'Column groups', align: 'right', value: (r) => r.columnGroups, render: (r) => (r.setUp ? String(r.columnGroups) : <span className="text-(--color-ink-4)">—</span>) },
      {
        key: 'hierarchy',
        header: 'Hierarchy type',
        align: 'left',
        sortValue: (r) => r.hierarchyMode ?? -1,
        render: (r) => (r.hierarchyMode === null ? <span className="text-(--color-ink-4)">—</span>
          : r.hierarchyMode === 0 ? <span className="text-(--color-ink-3)">Off</span>
            : <Badge tone={r.hierarchyMode === 2 ? 'warn' : 'neutral'}>{HIERARCHY[r.hierarchyMode] ?? '—'}</Badge>),
      },
      {
        key: 'phone',
        header: 'Phone Access',
        align: 'left',
        sortValue: (r) => (r.mobileAllowed === null ? -1 : r.mobileAllowed ? 1 : 0),
        render: (r) => (r.mobileAllowed === null ? <span className="text-(--color-ink-4)">—</span>
          : r.mobileAllowed ? <span className="text-(--color-good)">Allowed</span> : <span className="text-(--color-ink-3)">Not allowed</span>),
      },
    ],
    [],
  )

  const all = data ?? []
  const held = all.filter((r) => r.people > 0)
  const heldSetUp = held.filter((r) => r.setUp).length
  const share = held.length ? Math.round((heldSetUp / held.length) * 100) : 0

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {open !== null && (
        <RolePage api={api} id={open} roles={all} onBack={() => setOpen(null)} onChanged={() => setReload((n) => n + 1)} />
      )}

      <Card className={`flex min-h-0 flex-1 flex-col ${open !== null ? 'hidden' : ''}`}>
        <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
            <div className="flex min-w-[220px] flex-[1.4] flex-col gap-1 @6xl:max-w-[340px]">
              <span className="eyebrow">Find a role</span>
              <SearchInput label="Find an HR Role" placeholder="Role, function or number…" value={search} onChange={setSearch} />
            </div>
            <FilterSlot label="Function">
              <SearchSelect label="Function" value={fn} onChange={setFn} options={functionOptions} />
            </FilterSlot>
            <div className="flex flex-col gap-1">
              <span className="eyebrow">Show</span>
              <Segmented label="Show" value={show} onChange={setShow}
                options={[
                  { value: 'held', label: `Held by someone${data ? ` · ${held.length}` : ''}` },
                  { value: 'all', label: `All${data ? ` · ${all.length}` : ''}` },
                  { value: 'nobody', label: `Held by nobody${data ? ` · ${all.length - held.length}` : ''}` },
                ]} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="eyebrow">Only</span>
              <div className="flex h-8 items-center">
                <ToggleChip on={onlyNotSetUp} onChange={setOnlyNotSetUp}>Not configured yet</ToggleChip>
              </div>
            </div>
            {/* How far setting up has come: the job before the switch hour (D36). */}
            {data && held.length > 0 && (
              <div className="ml-auto flex min-w-[240px] flex-col gap-1.5">
                <span className="text-(length:--text-small) text-(--color-ink-3)">
                  Configured in MBI: <span className="font-semibold text-(--color-ink) tabular-nums">{heldSetUp} of {held.length}</span> roles held by users
                </span>
                <span aria-hidden className="h-1.5 w-full overflow-hidden rounded-full bg-(--color-surface-3)">
                  <span className={`block h-full rounded-full ${share === 100 ? 'bg-(--color-good)' : 'bg-(--color-navy-700)'}`} style={{ width: `${share}%` }} />
                </span>
              </div>
            )}
          </div>
        </div>

        {data && shown.length > 0 && (
          <div className="flex shrink-0 items-center gap-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
            <span className="flex-1">
              Showing {shown.length.toLocaleString('en-GB')} of {all.length.toLocaleString('en-GB')} roles from Mindzer. Open one to configure it.
            </span>
            <span ref={setChromeSlot} className="flex shrink-0 items-center" />
          </div>
        )}

        {failure ? (
          <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
        ) : data === null ? (
          <Loading label="Reading HR Roles…" />
        ) : data.length === 0 ? (
          <EmptyState title="Mindzer has sent no HR Roles yet." detail="Roles appear after the first hourly Mindzer update. See Mindzer sync report." />
        ) : shown.length === 0 ? (
          <EmptyState title="No role matches." detail="Clear the search or the function, or show all roles." />
        ) : (
          <GridChromeSlot.Provider value={chromeSlot}>
            <DataGrid fill gridId="access-hr-roles" columns={columns} rows={shown} onRowClick={(r) => setOpen(r.roleId)} />
          </GridChromeSlot.Provider>
        )}
      </Card>
    </div>
  )
}

/* =========================================================================
   THE ROLE'S PAGE
   ========================================================================= */

type Draft = { reports: string[]; groups: number[]; hierarchy: 'off' | 'type1' | 'type2'; phone: boolean }

const MODE: Record<number, Draft['hierarchy']> = { 0: 'off', 1: 'type1', 2: 'type2' }

const MODES: [Draft['hierarchy'], string, string][] = [
  ['off', 'Off', 'Every row in their companies, Business Units and Business Teams.'],
  ['type1', 'Type 1', 'Only their own and their staff’s rows, inside their own scopes.'],
  ['type2', 'Type 2', 'Their own and their staff’s rows, plus everything each staff member can see, with that staff member’s column groups.'],
]

function draftOf(page: AccessHrRolePage): Draft {
  return {
    reports: page.reports.filter((r) => r.allowed).map((r) => r.slug),
    groups: page.columnGroups.filter((g) => g.allowed).map((g) => g.groupId),
    hierarchy: MODE[page.role?.hierarchyMode ?? 0] ?? 'off',
    phone: page.role?.mobileAllowed ?? false,
  }
}

const sameDraft = (a: Draft, b: Draft) =>
  a.hierarchy === b.hierarchy && a.phone === b.phone
  && [...a.reports].sort().join() === [...b.reports].sort().join()
  && [...a.groups].sort().join() === [...b.groups].sort().join()

function RolePage({ api, id, roles, onBack, onChanged }: {
  api: Api; id: number; roles: AccessHrRoleRow[]; onBack: () => void; onChanged: () => void
}) {
  const [page, setPage] = useState<AccessHrRolePage | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [tab, setTab] = useState<'settings' | 'people' | 'history'>('settings')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null)
  const [effect, setEffect] = useState<{ draft: Draft; remove: boolean; answer: AccessHrRoleEffect } | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)
  const [people, setPeople] = useState<AccessPeople | null>(null)
  const [peopleFailure, setPeopleFailure] = useState<ApiFailure | null>(null)
  const [stale, setStale] = useState(false)
  // What hierarchy would change, before it goes on (D49).
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setFailure(null)
    setStale(false)
    api
      .accessHrRole(id)
      .then((p) => {
        if (cancelled) return
        setPage(p)
        setDraft(draftOf(p))
        setEffect(null)
        setCopiedFrom(null)
      })
      .catch((e: unknown) => !cancelled && setFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, id, reload])

  useEffect(() => {
    if (tab !== 'people' || people || peopleFailure) return
    let cancelled = false
    api.accessHrRolePeople(id).then((p) => !cancelled && setPeople(p)).catch((e: unknown) => !cancelled && setPeopleFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, id, tab, people, peopleFailure])

  const role = page?.role ?? null
  const loaded = page ? draftOf(page) : null
  const changed = !!draft && !!loaded && (!sameDraft(draft, loaded) || !role?.setUp)
  // Save is offered for exactly the draft that was reviewed.
  const reviewed = !!effect && !!draft && !effect.remove && sameDraft(effect.draft, draft)

  const body = (remove: boolean): AccessHrRoleBody => ({
    expectedVersion: role?.version ?? null,
    reports: draft?.reports ?? [],
    columnGroups: draft?.groups ?? [],
    // What each ticked group meant when this page read it: a group renamed or given
    // other columns since is refused on save (51637), and the page is read again.
    columnGroupKeys: Object.fromEntries(
      (draft?.groups ?? []).flatMap((g) => {
        const k = page?.columnGroups.find((x) => x.groupId === g)?.groupKey
        return k ? [[String(g), k]] : []
      }),
    ),
    hierarchy: draft?.hierarchy ?? 'off',
    phone: draft?.phone ?? false,
    reason: reason.trim() || undefined,
    remove,
  })

  const review = (remove: boolean) => {
    if (!draft || busy) return
    // A reason belongs to one action: one typed for Save never carries into Stop using.
    if (effect && effect.remove !== remove) setReason('')
    setBusy(true)
    setNotice(null)
    api
      .accessHrRolePreview(id, body(remove))
      .then((a) => setEffect({ draft, remove, answer: a }))
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const save = () => {
    if (!effect || busy || !reason.trim()) return
    setBusy(true)
    setNotice(null)
    const call = effect.remove ? api.accessHrRoleRemove(id, role?.version ?? null, reason.trim()) : api.accessHrRoleSave(id, body(false))
    call
      .then((a) => {
        setNotice({ tone: 'good', text: `${effect.remove ? 'Stopped using this role in MBI' : 'Saved'}. ${effectSentences(a.summary, effect.remove).join(' ')}` })
        setReason('')
        setPeople(null)
        setReload((n) => n + 1)
        onChanged()
      })
      .catch((e: unknown) => {
        const f = failureOf(e)
        if (f.kind === 'error' && f.reason === 'CHANGED_SINCE_READ') {
          // Saving again would be refused again: the way out is to read the role afresh.
          setEffect(null)
          setStale(true)
          setNotice({ tone: 'bad', text: 'Someone else changed this role since you opened it. Reload to see their change; yours is not saved.' })
        } else if (f.kind === 'error' && f.reason === 'COLUMN_GROUP_CHANGED') {
          // A group ticked here was renamed or given other columns on Column definition since:
          // the server's words name it. Read the role again before saving it.
          setEffect(null)
          setStale(true)
          setNotice({ tone: 'bad', text: `${f.title} Yours is not saved.` })
        } else {
          setNotice({ tone: 'bad', text: messageOf(e) })
        }
      })
      .finally(() => setBusy(false))
  }

  const copyFrom = (fromId: string) => {
    if (!fromId) return
    api
      .accessHrRole(Number(fromId))
      .then((p) => {
        setDraft(draftOf(p))
        setEffect(null)
        setCopiedFrom(p.role ? roleLabel(p.role) : `role ${fromId}`)
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
  }

  // A report taken off takes its column groups with it: a group goes only with its own report (D53, 51627).
  const groupsOf = (slugs: string[], groups: number[]) =>
    groups.filter((g) => slugs.includes(page?.columnGroups.find((x) => x.groupId === g)?.reportSlug ?? ''))
  const toggleReport = (slug: string) =>
    setDraft((d) => {
      if (!d) return d
      const reports = d.reports.includes(slug) ? d.reports.filter((x) => x !== slug) : [...d.reports, slug]
      return { ...d, reports, groups: groupsOf(reports, d.groups) }
    })
  const toggleGroup = (id: number) =>
    setDraft((d) => (d ? { ...d, groups: d.groups.includes(id) ? d.groups.filter((x) => x !== id) : [...d.groups, id] } : d))

  const title = role ? (role.roleName ? roleLabel(role) : `(no name) - ${role.roleFunction ?? ''}`) : null

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={onBack} className="rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline">
            ← HR Roles
          </button>
          {role && (
            <div className="w-64">
              <SearchSelect label="Copy settings from" value="" onChange={copyFrom} placeholder="Copy settings from…"
                options={[{ value: '', label: 'Copy settings from…' },
                  ...roles.filter((r) => r.setUp && r.roleId !== role.roleId).map((r) => ({ value: String(r.roleId), label: roleLabel(r) }))]} />
            </div>
          )}
        </div>
        {role && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">{title}</h2>
              {role.setUp ? <Badge tone="good">Configured in MBI</Badge> : <Badge tone="warn">Not configured</Badge>}
            </div>
            <dl className="flex flex-wrap gap-x-5 gap-y-1 text-(length:--text-small)">
              <Meta label="Role number" value={String(role.roleId)} />
              <Meta label="Held by" value={`${role.people} ${role.people === 1 ? 'user' : 'users'}`} />
              <Meta label="Last changed" value={role.setUp && role.version
                ? `${day(role.version)}${role.changedByEmail ? ` by ${role.changedByEmail}` : ''}` : 'never: nobody holding it opens anything until it is configured'} />
            </dl>
          </div>
        )}
        {notice && <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Notice>}
        {stale && (
          <div><Button variant="default" onClick={() => { setNotice(null); setReload((n) => n + 1) }}>Reload this role</Button></div>
        )}
        {copiedFrom && <Notice tone="info" onDismiss={() => setCopiedFrom(null)}>Copied from {copiedFrom}. Nothing is saved until you review and save.</Notice>}
        <PageTabs tabs={[{ id: 'settings' as const, label: 'Settings' }, { id: 'people' as const, label: `Users${role ? ` · ${role.people}` : ''}` },
          { id: 'history' as const, label: 'History' }]}
          value={tab} onChange={setTab} label="HR Role" />
      </div>

      {failure ? (
        <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
      ) : !page || !role || !draft || !loaded ? (
        <Loading label="Reading this role…" />
      ) : tab === 'history' ? (
        // Who changed this role, when and why (05 7.6; review R-26).
        <div className="min-h-0 flex-1 overflow-y-auto bg-(--color-surface-2) p-4"><HistoryTab api={api} id={id} role /></div>
      ) : tab === 'people' ? (
        peopleFailure ? <FailureState failure={peopleFailure} onRetry={() => setPeopleFailure(null)} />
        : people === null ? <Loading label="Reading who holds it…" /> : people.people.length === 0 ? (
          <EmptyState title="Nobody holds this role." detail="Mindzer gives this role to nobody who is active." />
        ) : (
          <ul className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto bg-(--color-surface-2) p-4" aria-label="Users who hold this role">
            {people.people.map((p) => (
              <li key={p.mindzerUserId} className="flex items-center gap-4 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-2 transition-colors hover:border-(--color-navy-300) hover:bg-(--color-navy-50)">
                <span className="min-w-0 flex-1"><PersonCell name={p.displayName} email={p.email ?? `Mindzer user ${p.mindzerUserId}`} /></span>
                <span className="w-16 text-(length:--text-small) text-(--color-ink-3)">{p.compId ?? '—'}</span>
                <span className="flex w-52 justify-end"><StatusBadge status={p.status} /></span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto bg-(--color-surface-2) @5xl:grid-cols-[1fr_360px] @5xl:overflow-hidden">
          <div className="flex min-h-0 flex-col gap-4 border-(--color-line) px-5 py-4 @5xl:overflow-y-auto @5xl:border-r">
            {/* Reports, each opening on its own column groups when ticked (owner, 29 Oct 2026; D53:
                "only the column groups for the sales report"). Groups are made on Column definition. */}
            <Panel>
              <ReportGroups
                reports={page.reports.map((r) => ({ code: r.slug, label: r.displayName }))}
                groups={page.columnGroups.map((g) => ({
                  code: String(g.groupId),
                  label: g.groupName,
                  reportSlug: g.reportSlug,
                  hint: g.description ?? (unlocksShort(g.unlocks) ?? undefined),
                  // What it shows, whole (review R-22).
                  title: g.unlocks ? `Shows ${g.unlocks}` : 'It shows no column yet: choose it on Column definition.',
                }))}
                chosenReports={draft.reports} chosenGroups={draft.groups.map(String)}
                onToggleReport={toggleReport}
                onSetReports={(codes) => setDraft((d) => (d ? { ...d, reports: codes, groups: groupsOf(codes, d.groups) } : d))}
                onToggleGroup={(c) => toggleGroup(Number(c))}
                onSetGroups={(slug, codes) => setDraft((d) => (d ? {
                  ...d,
                  groups: [...d.groups.filter((x) => !page.columnGroups.some((g) => g.groupId === x && g.reportSlug === slug)), ...codes.map(Number)],
                } : d))}
                reportChanged={(c) => draft.reports.includes(c) !== loaded.reports.includes(c)}
                groupChanged={(c) => draft.groups.includes(Number(c)) !== loaded.groups.includes(Number(c))} />
            </Panel>

            <Panel title="Hierarchy">
              <div role="radiogroup" aria-label="Hierarchy" className="grid grid-cols-1 gap-2 @3xl:grid-cols-3"
                // One tab stop; the arrows move the choice, as a radio group's do.
                onKeyDown={(e) => {
                  const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
                  if (!step) return
                  e.preventDefault()
                  const to = (MODES.findIndex(([v]) => v === draft.hierarchy) + step + MODES.length) % MODES.length
                  setDraft((d) => (d ? { ...d, hierarchy: MODES[to][0] } : d))
                  e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[to]?.focus()
                }}>
                {MODES.map(([value, name, words]) => {
                  const on = draft.hierarchy === value
                  return (
                    <button key={value} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
                      onClick={() => setDraft((d) => (d ? { ...d, hierarchy: value } : d))}
                      className={`flex flex-col items-start gap-1 rounded-md border px-3 py-2.5 text-left transition-colors ${
                        on ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)'
                          : 'border-(--color-line-strong) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'}`}>
                      <span className="flex w-full items-center gap-2">
                        <span aria-hidden className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${on ? 'border-(--color-navy-700)' : 'border-(--color-ink-4)'}`}>
                          {on && <span className="h-2 w-2 rounded-full bg-(--color-navy-700)" />}
                        </span>
                        <span className={`font-semibold ${on ? 'text-(--color-navy-700)' : 'text-(--color-ink)'}`}>{name}</span>
                        {/* The mode chosen now is Added, the one saved is Removed. */}
                        {draft.hierarchy !== loaded.hierarchy && (on || value === loaded.hierarchy) && <ChangedMark on={on} className="ml-auto" />}
                      </span>
                      <span className="text-(length:--text-small) text-(--color-ink-2)">{words}</span>
                    </button>
                  )
                })}
              </div>
              {/* Before a role's hierarchy goes on, every holder's rows now and after (D49; owner, 28 Oct 2026). */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-(length:--text-small)">
                <Button size="sm" variant="ghost" onClick={() => setPreviewing(true)}>Preview what hierarchy changes…</Button>
                <span className="text-(--color-ink-3)">Each holder&apos;s rows per report now, with Type 1 and with Type 2. Counts only.</span>
              </div>
              {previewing && page.role && (
                <HierarchyPreview api={api} roleId={id} roleName={`${page.role.roleName ?? `HR Role ${id}`}${page.role.roleFunction ? ` - ${page.role.roleFunction}` : ''}`}
                  onClose={() => setPreviewing(false)} />
              )}
              {draft.hierarchy === 'type2' && (
                <p className="rounded-md border border-(--color-warn)/30 bg-(--color-warn)/8 px-3 py-2 text-(length:--text-small) text-(--color-ink)">
                  Type 2 gives each holder everything their staff can see, in any company. One wrong manager link in Mindzer hands a whole view to the wrong user.
                </p>
              )}
            </Panel>

            <Panel title="Phone">
              <Switch on={draft.phone} onChange={(on) => setDraft((d) => (d ? { ...d, phone: on } : d))}
                label="Holders can open MBI on a phone" changed={draft.phone !== loaded.phone}
                detail={draft.phone ? 'They can use MBI on a phone as on a laptop.' : 'On a phone they get the one refusal message.'} />
            </Panel>

            {role.setUp && (
              <section className="flex flex-wrap items-center gap-3 rounded-md border border-(--color-overdue)/30 bg-(--color-surface) px-4 py-3">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-(length:--text-small) font-semibold text-(--color-ink)">Stop using this role in MBI</span>
                  <span className="text-(length:--text-small) text-(--color-ink-3)">Its holders lose what it gives them. Review shows exactly who, before anything is saved.</span>
                </span>
                <Button variant="danger" onClick={() => review(true)} disabled={busy}>Stop using it…</Button>
              </section>
            )}
          </div>

          <div className="flex min-h-0 flex-col gap-3 px-4 py-4 @5xl:overflow-y-auto">
            <Panel title={effect?.remove ? 'What stopping it does' : 'What saving does'}>
              {!effect ? (
                <p className="text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
                  {changed ? 'Press Review changes to see who gains or loses what, before anything is saved.' : 'No change yet. Tick, untick or pick above.'}
                </p>
              ) : (
                <EffectWords summary={effect.answer.summary} remove={effect.remove} stale={!effect.remove && !reviewed} />
              )}
              {(!effect || (!effect.remove && !reviewed)) && (
                <Button full variant="primary" onClick={() => review(false)} disabled={busy || !changed}>Review changes</Button>
              )}
              {(reviewed || effect?.remove) && (
                <>
                  <label className="flex flex-col gap-1 text-(length:--text-small) font-medium text-(--color-ink-2)">
                    Reason Of The Change (required)
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                      placeholder="Kept with the change, in the role's history."
                      className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 font-normal text-(--color-ink)" />
                  </label>
                  <div className="flex gap-2">
                    <Button full variant={effect?.remove ? 'danger' : 'primary'} onClick={save} disabled={busy || !reason.trim()}>
                      {effect?.remove ? 'Stop using this role' : 'Save changes'}
                    </Button>
                    <Button variant="ghost" onClick={() => { setEffect(null); if (effect?.remove) return; setDraft(loaded); setCopiedFrom(null) }}>
                      {effect?.remove ? 'Keep it' : 'Discard'}
                    </Button>
                  </div>
                </>
              )}
            </Panel>
          </div>
        </div>
      )}
    </Card>
  )
}

/** The server's answer as sentences (7.6): what saving will do, to whom. */
function EffectWords({ summary, remove, stale }: { summary: AccessHrRoleSummary | null; remove: boolean; stale: boolean }) {
  if (!summary) return <p className="text-(length:--text-small) text-(--color-ink-3)">The server sent no effect.</p>
  const lines = effectSentences(summary, remove)
  const widens = summary.Widens === true || summary.Widens === 1
  return (
    <div className="flex flex-col gap-2 text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
      {stale && <p className="font-medium text-(--color-overdue)">You changed the settings since: review again before saving.</p>}
      <p>{summary.Holders} {summary.Holders === 1 ? 'user holds' : 'users hold'} this role ({summary.Holders_Approved} approved).</p>
      <ul className="flex flex-col gap-1 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2">
        {lines.map((l, i) => <li key={i}>{l}</li>)}
      </ul>
      {widens ? (
        <p className="font-semibold text-(--color-warn)">⚠ This widens access.</p>
      ) : summary.People_Narrowed > 0 ? (
        <p className="font-semibold text-(--color-ink)">This narrows access.</p>
      ) : null}
    </div>
  )
}

/**
 * The counts as sentences, saying no more than they mean (review, 26 Oct 2026):
 * the builder's counts take in type-2 managers above the holders as well, and its
 * effect has no phone, so a phone-only change moves nobody's rows yet is a change.
 */
export function effectSentences(s: AccessHrRoleSummary | null, remove: boolean): string[] {
  if (!s) return []
  const out: string[] = []
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`
  if (s.People_Changed === 0 && s.Phone_Changed === 0 && s.Hierarchy_Changed === 0 && s.Kept_By_Personal_Setting === 0)
    return ['No change: nobody’s access moves.']
  if (s.Entry_Lost > 0) out.push(remove
    ? `${n(s.Entry_Lost, 'user', 'users')} will lose access to MBI at once.`
    : `${n(s.Entry_Lost, 'user', 'users')} can no longer enter MBI.`)
  if (s.Entry_Gained > 0) out.push(`Let ${n(s.Entry_Gained, 'user', 'users')} into MBI.`)
  if (s.Reports_Gained > 0) out.push(`${n(s.Reports_Gained, 'report opening', 'report openings')} gained, by holders and any managers above them.`)
  if (s.Reports_Lost > 0) out.push(`${n(s.Reports_Lost, 'report opening', 'report openings')} taken away.`)
  if (s.Column_Groups_Gained > 0) out.push(`${n(s.Column_Groups_Gained, 'user gains', 'users gain')} a column group.`)
  if (s.Companies_Gained > 0) out.push(`${n(s.Companies_Gained, 'user reaches', 'users reach')} a company they did not before.`)
  if (s.Hierarchy_Changed > 0) out.push(`Hierarchy changes for ${n(s.Hierarchy_Changed, 'holder', 'holders')}.`)
  if (s.Phone_Changed > 0) out.push(`Phone access changes for ${n(s.Phone_Changed, 'holder', 'holders')}.`)
  if (s.Managers_Affected > 0) out.push(`${n(s.Managers_Affected, 'manager', 'managers')} above them (hierarchy type 2) ${s.Managers_Affected === 1 ? 'is' : 'are'} affected too.`)
  if (s.Kept_By_Personal_Setting > 0) out.push(`${n(s.Kept_By_Personal_Setting, 'user keeps', 'users keep')} their own override.`)
  return out
}

/**
 * What a column group shows, in a tile's room (review R-22): three columns, then how
 * many more. A group is one report's (D53), so the server's text is its columns alone
 * ("Cost, GP, GP%"); the tile's title carries it whole.
 */
export function unlocksShort(unlocks: string | null | undefined): string | null {
  if (!unlocks) return null
  const cols = unlocks.split(', ')
  return `Shows ${cols.slice(0, 3).join(', ')}${cols.length > 3 ? ` +${cols.length - 3} more` : ''}`
}
